import { jwtDecode } from "jwt-decode";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import Spinner from "../spinner/Spinner";
import OrderDetailAddress from "./OrderDetailAddress";

import { AddOnProps, TokenPayload } from "../../types/ComponentProps";

import ProductDetailsModalStyles from "./ProductDetailsModalStyles.module.css";

import createOrder from "../../api/createOrder";

import useGetUserPoint from "../../hooks/useGetUserPoint";
import useGetProductById from "../../hooks/useGetProductById";
import { useGetOrderCooldown } from "../../hooks/useGetOrderCooldown";

type ProductDetailsModalProps = {
  activeProduct: number | null;
  productId?: number;
  productType?: string;
  closeModalClickHandler: () => void;
};

type Option = {
  id: string;
  title: string;
  price?: number | null;
  quantity?: number;
};

type Variant = {
  id: string;
  title: string;
  options: Option[];
};

type NormalizedVariant = {
  title: string;
  options: Option[];
};

type OrderFormState = {
  fullName: string;
  email: string;
  mobilePhone: string;
  city: string;
  province: string;
  postalCode: string;
  landmarks: string;
  orderDetails: {
    addOns?: AddOnProps[];
    variant?: Record<string, Option>;
    [key: string]: any;
  } | null;
};

const ProductDetailsModal = ({
  productId,
  productType,
  closeModalClickHandler,
  activeProduct,
}: ProductDetailsModalProps) => {
  const { data } = useGetUserPoint();
  const accessToken = localStorage.getItem("accessTokenGC");
  const userDetails = accessToken ? jwtDecode<TokenPayload>(accessToken) : null;
  const queryClient = useQueryClient();
  const { dataGetProductById, isLoadingGetProductById } = useGetProductById(
    Number(productId),
    String(productType),
  );

  const [loadingOrder, setLoadingOrder] = useState({
    message: "",
    open: false,
  });

  const orderFormInitialState = {
    fullName: `${userDetails?.firstName} ${userDetails?.lastName}`,
    email: `${userDetails?.email}`,
    mobilePhone: "",
    city: "",
    province: "",
    postalCode: "",
    landmarks: "",
    orderDetails: null,
    orderStatus: 0,
  };

  const [imageLoaded, setImageLoaded] = useState(false);
  const handleImageLoad = () => setImageLoaded(true);
  const handleImageError = () => setImageLoaded(false);
  const [orderForm, setOrderForm] = useState<OrderFormState>(
    orderFormInitialState,
  );
  const [selectedAddOns, setSelectedAddOns] = useState<AddOnProps[]>([]);
  const [selectedVariants, setSelectedVariants] = useState<
    Record<string, Option | null>
  >({});
  const [addressPage, setAddressPage] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<string | null>();

  const { cooldown } = useGetOrderCooldown();
  const cooldownLeft = cooldown?.secondsLeft ?? 0;

  const productVariants = dataGetProductById?.product?.variant;
  const isSuccessMessage =
    loadingOrder?.message === "Your order has been placed successfully.";
    
  // Check if product is out of stock (for products without variants)
  const productQuantity = dataGetProductById?.product?.quantity;
  const hasVariantsArray = productVariants && Array.isArray(productVariants) && productVariants.length > 0;
  
  const hasNoVariantsAndOutOfStock = 
    dataGetProductById?.product?.type === "Merch" && 
    !hasVariantsArray &&
    productQuantity !== undefined && 
    productQuantity !== null && 
    productQuantity <= 0;
    
  // Normalize productVariants - preserve quantity field for stock tracking
  const normalizedVariants: NormalizedVariant[] = useMemo(() => {
    if (!productVariants) return [];

    if (
      Array.isArray(productVariants) &&
      typeof productVariants[0] === "object" &&
      "options" in productVariants[0]
    ) {
      // Explicitly map to preserve all fields including quantity
      return productVariants.map((variant: any) => ({
        id: variant.id,
        title: variant.title,
        options: (variant.options || []).map((option: any) => ({
          id: option.id ?? String(option.id),
          title: option.title,
          price: option.price,
          quantity: option.quantity, // Preserve quantity for stock checking
        })),
      }));
    }

    if (Array.isArray(productVariants) && Array.isArray(productVariants[0])) {
      return [
        {
          title: "Options:",
          options: productVariants.map(([id, value]: [number, number]) => ({
            id: String(id),
            title: String(value),
            price: value,
            quantity: undefined, // No quantity info in this format
          })),
        },
      ];
    }

    return [];
  }, [productVariants]);

  // Check if Merch product has variants but user hasn't selected one yet
  const hasVariantsButNoSelection = 
    dataGetProductById?.product?.type === "Merch" && 
    normalizedVariants.length > 0 && 
    Object.keys(selectedVariants).length === 0;

  useEffect(() => {
    if (dataGetProductById?.product) {
      setOrderForm((previous: OrderFormState) => ({
        ...previous,
        orderDetails: {
          ...dataGetProductById.product,
          addOns: [],
          variant: {},
        },
      }));
    }
  }, [activeProduct, dataGetProductById]);

  const createOrderMutation = useMutation({
    mutationFn: () => createOrder(orderForm),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getUserPoint"] });
      // Invalidate the product query to refresh stock after order
      await queryClient.invalidateQueries({ queryKey: ["getProductById", productId] });
      // Also invalidate the products list for admin view
      await queryClient.invalidateQueries({ queryKey: ["getAllProducts"] });

      setLoadingOrder({
        open: false,
        message: "Your order has been placed successfully.",
      });

      setTimeout(() => {
        setLoadingOrder({ open: false, message: "" });
        closeModalClickHandler();
      }, 2000);
    },
    onError: (error: Error) => {
      console.error("Error creating product:", error.message);
      setLoadingOrder({
        open: false,
        message: error.message,
      });
    },
  });

  const placeOrder = () => {
    if (cooldownLeft > 0) {
      return setLoadingOrder({
        open: false,
        message: "Please wait 1 minute before claiming again.",
      });
    }

    // Check stock for selected variant (Merch with variants) or overall product quantity (Merch without variants / Vouchers)
    const hasVariants = normalizedVariants.length > 0;
    const selectedVariantOption = Object.values(selectedVariants)[0];
    const variantQuantity = selectedVariantOption?.quantity;
    const productQuantity = dataGetProductById?.product?.quantity;
    
    let isOutOfStock = false;
    
    if (hasVariants && variantQuantity !== undefined) {
      // For products with variants, check the selected variant's quantity
      isOutOfStock = variantQuantity <= 0;
    } else {
      // For products without variants, check the product's quantity
      isOutOfStock = productQuantity !== undefined && productQuantity !== null && productQuantity <= 0;
    }

    if (isOutOfStock) {
      return setLoadingOrder({
        open: false,
        message: "Sorry, the item is out of stock.",
      });
    }

    if (data?.points?.coins < (orderForm.orderDetails?.price ?? 0)) {
      return setLoadingOrder({
        open: false,
        message: "You don't have enough coins to claim this product.",
      });
    }

    setLoadingOrder({ open: true, message: "" });

    createOrderMutation.mutate();
  };

  const onClaimNowSubmitHandler = () => {
    if (dataGetProductById?.product?.type === "Merch") {
      // Only require variant selection if the product HAS variants
      if (normalizedVariants.length > 0) {
        const missing = normalizedVariants.filter(
          (v) => !selectedVariants[v.title],
        );

        if (missing.length > 0) {
          return setLoadingOrder({
            open: false,
            message: "Please select a size to proceed.",
          });
        }
      }

      setAddressPage(true);
      return;
    }

    placeOrder();
  };

  const confirmOrderDetails = () => {
    placeOrder();
  };

  const onVariantOptionClick = (variant: Option, variantTitle: string) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [variantTitle]: variant,
    }));

    if (variant.price != null) {
      setSelectedPrice(variant.price);
    }

    setOrderForm((previous: OrderFormState) => ({
      ...previous,
      orderDetails: {
        ...previous.orderDetails,
        variant: {
          ...(previous.orderDetails?.variant || {}),
          [variantTitle]: {
            id: variant.id,
            title: variant.title,
            price: variant.price,
            quantity: variant.quantity,
          },
        },
        price:
          dataGetProductById?.product?.price ??
          variant.price ??
          previous.orderDetails?.price,
      },
    }));
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    addOn: AddOnProps,
  ) => {
    setSelectedAddOns((prevSelected) => {
      if (e.target.checked) {
        return [...prevSelected, addOn];
      } else {
        return prevSelected.filter(
          (item) => item.addonName !== addOn.addonName,
        );
      }
    });
    setOrderForm((previous: OrderFormState) => ({
      ...previous,
      orderDetails: {
        ...previous.orderDetails,
        addOns: e.target.checked
          ? [...(previous.orderDetails?.addOns || []), addOn]
          : (previous.orderDetails?.addOns || []).filter(
              (item) => item.addonName !== addOn.addonName,
            ),
      },
    }));
  };

  if (addressPage)
    return (
      <OrderDetailAddress
        order={orderForm}
        setOrderForm={setOrderForm}
        setLoadingOrder={setLoadingOrder}
        loadingOrder={loadingOrder}
        confirmOrderDetails={confirmOrderDetails}
        cooldownLeft={cooldownLeft}
        onClose={() => {
          setAddressPage(false);
          closeModalClickHandler();
        }}
      />
    );

  return (
    <div
      onClick={closeModalClickHandler}
      className={ProductDetailsModalStyles.modalDetailsProductContainer}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={ProductDetailsModalStyles.modalMainContent}
      >
        {isLoadingGetProductById ? (
          <Spinner alignSpin="center-spinner" variant="medium-spinner" />
        ) : (
          <div className={ProductDetailsModalStyles.contentBlk}>
            {/* Header Section */}
            <div>
              <div className={`${ProductDetailsModalStyles.flexBoxBlk}`}>
                <div>
                  <h1>{dataGetProductById?.product?.productName}</h1>
                </div>
                <div>
                  <button
                    onClick={closeModalClickHandler}
                    className={`fa-solid fa-xmark
                ${ProductDetailsModalStyles.closeIconCreateBadge}`}
                    aria-label="Close"
                  ></button>
                </div>
              </div>
              <hr />
            </div>
            {/* Content Section */}
            <div className={ProductDetailsModalStyles.descriptionBlk}>
              {isLoadingGetProductById ? (
                <div className={ProductDetailsModalStyles.spinnerBlkDiv}>
                  <Spinner
                    variant="medium-spinner"
                    alignSpin="center-spinner"
                  />
                </div>
              ) : (
                <>
                  {!imageLoaded && (
                    <div className={ProductDetailsModalStyles.spinnerBlkDiv}>
                      <Spinner
                        variant="medium-spinner"
                        alignSpin="center-spinner"
                      />
                    </div>
                  )}
                  <img
                    src={dataGetProductById?.product?.imageURL}
                    alt={dataGetProductById?.product?.productName}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={{ display: imageLoaded ? "block" : "none" }}
                  />

                  <div className={ProductDetailsModalStyles.merchDetailsBlk}>
                    {/* Amount */}
                    <div className={ProductDetailsModalStyles.headerFlexBoxBlk}>
                      <div>
                        <h2>Amount</h2>
                      </div>
                      <div className={ProductDetailsModalStyles.priceSection}>
                        <h1>
                          {dataGetProductById?.product?.price ?? selectedPrice}{" "}
                          Coins
                        </h1>
                      </div>
                    </div>

                    {/* Add Ons */}
                    {dataGetProductById?.product?.addOns?.length > 0 && (
                      <>
                        <hr
                          className={ProductDetailsModalStyles.horizontalLine}
                          style={{ marginBottom: "25px" }}
                        />
                        <div
                          className={ProductDetailsModalStyles.addOnsDetailsBlk}
                        >
                          <h2>Add ons</h2>
                          <div
                            className={ProductDetailsModalStyles.addOnListBlk}
                          >
                            {dataGetProductById.product.addOns.map(
                              (data: AddOnProps, index: number) => (
                                <div
                                  className={
                                    ProductDetailsModalStyles.addOnCardList
                                  }
                                  key={index}
                                >
                                  <div>
                                    <input
                                      type="checkbox"
                                      checked={selectedAddOns.some(
                                        (item: any) =>
                                          item.addonName === data?.addonName,
                                      )}
                                      onChange={(e) =>
                                        handleCheckboxChange(e, data)
                                      }
                                    />
                                  </div>
                                  <div>
                                    <h3>{data?.addonName}</h3>
                                  </div>
                                  <div>
                                    <h3>PHP {data?.price}</h3>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Variants */}
                    {normalizedVariants.length > 0 && (
                      <div
                        className={ProductDetailsModalStyles.variantSectionBlk}
                      >
                        {normalizedVariants.map((data, index) => (
                          <div key={index}>
                            <h1>{data.title}</h1>
                            <div className={ProductDetailsModalStyles.options}>
                              {data.options.map((option) => {
                                // Check if quantity exists and is 0 or less (out of stock)
                                const hasQuantityInfo = option.quantity !== undefined && option.quantity !== null;
                                const isOutOfStock = hasQuantityInfo && (option.quantity as number) <= 0;
                                const stockText = hasQuantityInfo 
                                  ? (isOutOfStock ? "Out of stock" : `${option.quantity} left`)
                                  : null;
                                
                                return (
                                  <button
                                    type="button"
                                    key={option.id}
                                    onClick={() => {
                                      if (!isOutOfStock) {
                                        onVariantOptionClick(option, data.title);
                                        setSelectedVariant(option.title);
                                      }
                                    }}
                                    disabled={isOutOfStock}
                                    aria-disabled={isOutOfStock}
                                    className={`
                                      ${selectedVariants[data.title]?.id === option.id
                                        ? ProductDetailsModalStyles.selectedVariant
                                        : ""}
                                      ${isOutOfStock ? ProductDetailsModalStyles.outOfStockVariant : ""}
                                    `}
                                    aria-label={`${option.title}${stockText ? `, ${stockText}` : ""}`}
                                  >
                                    <span className={ProductDetailsModalStyles.variantTitle}>
                                      {productType === "Custom Voucher" && "₱"}
                                      {option.title}
                                    </span>
                                    {stockText && (
                                      <span 
                                        className={`${ProductDetailsModalStyles.stockIndicator} ${
                                          isOutOfStock 
                                            ? ProductDetailsModalStyles.stockOut 
                                            : ProductDetailsModalStyles.stockAvailable
                                        }`}
                                      >
                                        {stockText}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {productType !== "Custom Voucher" && (
                      <>
                        <hr
                          className={ProductDetailsModalStyles.horizontalLine}
                          style={{ marginBottom: "24px", marginTop: "24px" }}
                        />

                        <p>{dataGetProductById?.product?.productDescription}</p>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer Section */}
            <div className={ProductDetailsModalStyles.footerSection}>
              {loadingOrder?.message && (
                <div
                  className={ProductDetailsModalStyles.message}
                  style={{
                    border:
                      loadingOrder?.message && !isSuccessMessage
                        ? "1px solid #FDA29B"
                        : "",
                  }}
                >
                  {!isSuccessMessage && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      fill="none"
                    >
                      <path
                        d="M9.16659 5.83337V9.16671M9.16659 12.5H9.17492M17.4999 9.16671C17.4999 13.7691 13.769 17.5 9.16659 17.5C4.56421 17.5 0.833252 13.7691 0.833252 9.16671C0.833252 4.56433 4.56421 0.833374 9.16659 0.833374C13.769 0.833374 17.4999 4.56433 17.4999 9.16671Z"
                        stroke="#D92D20"
                        stroke-width="1.66667"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  )}
                  <p
                    className={
                      isSuccessMessage
                        ? ProductDetailsModalStyles.successMessage
                        : ProductDetailsModalStyles.errorMessage
                    }
                  >
                    {loadingOrder?.message}
                  </p>
                </div>
              )}

              <div className={ProductDetailsModalStyles.claimButtonSection}>
                <button
                  disabled={loadingOrder.open || cooldownLeft > 0 || hasNoVariantsAndOutOfStock || hasVariantsButNoSelection}
                  onClick={onClaimNowSubmitHandler}
                  style={{
                    opacity: cooldownLeft > 0 || hasNoVariantsAndOutOfStock || hasVariantsButNoSelection ? "50%" : "",
                    cursor: cooldownLeft > 0 || hasNoVariantsAndOutOfStock || hasVariantsButNoSelection ? "default" : "",
                    background:
                      loadingOrder?.message && !isSuccessMessage
                        ? "#F9FAFB"
                        : "",
                    color:
                      loadingOrder?.message && !isSuccessMessage
                        ? "#475467"
                        : "",
                  }}
                >
                  {loadingOrder.open ? (
                    <Spinner
                      variant="small-spinner"
                      alignSpin="center-spinner"
                    />
                  ) : (
                    <>
                      {hasNoVariantsAndOutOfStock ? (
                        "Out of Stock"
                      ) : hasVariantsButNoSelection ? (
                        "Select a size to claim"
                      ) : cooldownLeft > 0 ? (
                        `Please wait ${cooldownLeft}s`
                      ) : (
                        <>
                          Claim{" "}
                          {normalizedVariants.length > 0 && selectedVariant &&
                            `${productType === "Custom Voucher" ? "₱" : ""}${selectedVariant}`}{" "}
                          Now
                          <i
                            className="fa-solid fa-arrow-right"
                            style={{
                              color:
                                loadingOrder?.message && !isSuccessMessage
                                  ? "#475467"
                                  : "inherit",
                            }}
                          ></i>
                        </>
                      )}
                    </>
                  )}
                </button>

                <p>Once you claim this we cannot issue a refund</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailsModal;
